import React, { useState } from "react";
import {
  Box,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Paper,
  CircularProgress,
  Switch,
  Pagination,
  Grid,
  Snackbar,
  Alert,
} from "@mui/material";

import { withAdminAuth } from "../../HOC";
import {
  useGetAllUsersQuery,
  useToggleUserStatusMutation,
} from "../../Apis/authApi";

function Users() {
  const [page, setPage] = useState(1);
  const pageSize = 10;
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [loadingRow, setLoadingRow] = useState<string | null>(null);
  const [snackbar, setSnackbar] = useState<{
      open: boolean;
      message: string;
      severity: "success" | "error" | "info";
    }>({ open: false, message: "", severity: "info" });
  
    const handleCloseSnackbar = () => {
      setSnackbar({ ...snackbar, open: false });
    };

  const { data, isFetching, refetch } = useGetAllUsersQuery({
    page,
    pageSize,
    search,
  });
  const [toggleUserStatus] = useToggleUserStatusMutation();

  const users = data?.result?.users || [];
  const pagination = data?.result?.pagination || {};

  const handleToggle = async (userId: string, currentStatus: boolean) => {
    try {
      setLoadingRow(userId);
      await toggleUserStatus({ userId, toggle: !currentStatus }).unwrap();
      setSnackbar({
        open: true,
        message: `User ${!currentStatus ? "activated" : "deactivated"} successfully`,
        severity: "success",
      });
      
      refetch();
    } catch (error) {
      setSnackbar({
        open: true,
        message: "Failed to update user status",
        severity: "error",
      });
      
    } finally {
      setLoadingRow(null);
    }
  };

  const filteredUsers = users.filter((u: any) => {
    if (statusFilter === "active") return u.isActive;
    if (statusFilter === "inactive") return !u.isActive;
    return true;
  });

  return (
    <Box sx={{ p: 3 }}>
      {/* Filters */}
      <Grid container spacing={2} sx={{ mb: 2 }}>
        <Grid sx={{ width: { xs: "100%", md: "calc(33.333% - 16px)" } }}>
          <TextField
            fullWidth
            label="Search by name, email, entity..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </Grid>

        <Grid sx={{ width: { xs: "100%", md: "calc(25% - 16px)" } }}>
          <FormControl fullWidth>
            <InputLabel>Status Filter</InputLabel>
            <Select
              value={statusFilter}
              label="Status Filter"
              onChange={(e) => setStatusFilter(e.target.value)}
            >
              <MenuItem value="all">All Users</MenuItem>
              <MenuItem value="active">Active Only</MenuItem>
              <MenuItem value="inactive">Inactive Only</MenuItem>
            </Select>
          </FormControl>
        </Grid>
      </Grid>

      {/* Table */}
      {isFetching ? (
        <Box sx={{ textAlign: "center", py: 5 }}>
          <CircularProgress />
        </Box>
      ) : (
        <Paper sx={{ overflowX: "auto" }}>
          <Table size="small">
            <TableHead sx={{ backgroundColor: "rgba(81, 35, 132, 1)"}}>
              <TableRow>
                
                <TableCell sx={{ color: "white", fontWeight: "bold" }}>Full Name</TableCell>
                <TableCell sx={{ color: "white", fontWeight: "bold" }}>Email</TableCell>
                <TableCell sx={{ color: "white", fontWeight: "bold" }}>Phone</TableCell>
                <TableCell sx={{ color: "white", fontWeight: "bold" }}>Entity</TableCell>
                <TableCell sx={{ color: "white", fontWeight: "bold" }}>Status</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredUsers.map((user: any) => (
                <TableRow key={user.id}>                  
                  <TableCell sx={{ fontWeight: "bold" }}>{user.fullName}</TableCell>
                  <TableCell>{user.email}</TableCell>
                  <TableCell>{user.phoneNumber}</TableCell>
                  <TableCell>{user.entity}</TableCell>
                  <TableCell>
                    <Switch
                      checked={user.isActive}
                      disabled={loadingRow === user.id}
                      onChange={() => handleToggle(user.id, user.isActive)}
                      color="success"
                    />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Paper>
      )}

      {/* Pagination */}
      {pagination?.totalPages > 1 && (
        <Box sx={{ mt: 3, display: "flex", justifyContent: "center" }}>
          <Pagination
            count={pagination.totalPages}
            page={page}
            onChange={(e, value) => setPage(value)}
            color="primary"
            shape="rounded"
            size="medium"
          />
        </Box>
      )}
            <Snackbar
              open={snackbar.open}
              autoHideDuration={5000}
              onClose={handleCloseSnackbar}
              anchorOrigin={{ vertical: "top", horizontal: "center" }}
            >
              <Alert
                onClose={handleCloseSnackbar}
                severity={snackbar.severity}
                sx={{ width: "100%" }}
              >
                {snackbar.message}
              </Alert>
            </Snackbar>
    </Box>
  );
}

export default withAdminAuth(Users);
